import { LengthUnit } from '@fulcrum/lib';
import { JSX, createMemo } from 'solid-js';
import {
  AlignmentSettings,
  AlignmentCoordinateOrder,
  ALIGNMENT_COORDINATE_ORDER_OPTIONS,
  LabelSymbol,
  LabelFormat,
  getLabelFormatOptions,
  migrateLabelFormat,
  DEFAULT_ALIGNMENT_SETTINGS,
} from '@trimble/types';
import { ISelectOption } from '@trimble-oss/moduswebcomponents';
import { t } from '@trimble/i18n';
import { convertLength } from '@trimble/utilities';

/** Minimum allowed value for the label size field. */
const MIN_LABEL_SIZE = 0.1;

/** Minimum allowed value for the label interval field (in display units). */
const MIN_LABEL_INTERVAL = 1;

/** Significant digits kept when displaying converted length values. */
const LENGTH_DISPLAY_PRECISION = 7;

const SYMBOL_OPTIONS: ISelectOption[] = [
  { label: '+', value: '+' },
  { label: '/', value: '/' },
];

export interface AlignmentSettingsPanelProps {
  settings: AlignmentSettings;
  onSettingsChanged(settings: AlignmentSettings): void;
  lengthUnit: LengthUnit;
}

export const AlignmentSettingsPanel = (
  props: AlignmentSettingsPanelProps
): JSX.Element => {
  const toDisplay = (meters: number): number =>
    parseFloat(
      convertLength(meters, 'm', props.lengthUnit).toPrecision(
        LENGTH_DISPLAY_PRECISION
      )
    );

  const toMeters = (displayValue: number): number =>
    convertLength(displayValue, props.lengthUnit, 'm');

  const unitSuffix = createMemo(() => ` (${props.lengthUnit})`);

  const formatSelectOptions = createMemo<ISelectOption[]>(() =>
    getLabelFormatOptions(props.settings.labelSymbol).map((fmt) => ({
      label: fmt,
      value: fmt,
    }))
  );

  const coordinateOrderOptions = createMemo<ISelectOption[]>(() =>
    (
      Object.entries(ALIGNMENT_COORDINATE_ORDER_OPTIONS) as [
        AlignmentCoordinateOrder,
        string,
      ][]
    ).map(([key, label]) => ({ label, value: key }))
  );

  const changeField = <K extends keyof AlignmentSettings>(
    key: K,
    value: AlignmentSettings[K]
  ): void => {
    props.onSettingsChanged({ ...props.settings, [key]: value });
  };

  const handleSymbolChange = (newSymbol: LabelSymbol): void => {
    const migratedFormat = migrateLabelFormat(
      props.settings.labelFormat,
      newSymbol
    );
    props.onSettingsChanged({
      ...props.settings,
      labelSymbol: newSymbol,
      labelFormat: migratedFormat,
    });
  };

  /** Read the selected value from a modus-wc-select inputChange event. */
  const getSelectValue = (event: Event): string => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Stencil updates the host element's value before emitting
    return (event.target as any).value as string;
  };

  const LENGTH_FIELDS: ReadonlySet<keyof AlignmentSettings> = new Set([
    'verticalOffset',
    'horizontalOffset',
    'labelInterval',
  ]);

  /**
   * Commit and validate the current value of a numeric field.
   * Re-used by both the Enter-key and blur handlers.
   *
   * Length fields are displayed in the user's length unit but stored in meters.
   */
  const commitNumberValue = (
    key: keyof AlignmentSettings,
    eventTarget: HTMLElement
  ): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Reading value from inner input or host element
    const rawValue = (eventTarget as any).value as string;
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed)) return;

    let displayValue = parsed;

    // labelInterval must be >= 1 (in display units); invalid values reset to default converted to display unit
    if (key === 'labelInterval' && parsed < MIN_LABEL_INTERVAL) {
      displayValue = toDisplay(DEFAULT_ALIGNMENT_SETTINGS.labelInterval);
    }

    // labelSize must be >= 0.1
    if (key === 'labelSize' && parsed < MIN_LABEL_SIZE) {
      displayValue = MIN_LABEL_SIZE;
    }

    const storedValue = LENGTH_FIELDS.has(key)
      ? toMeters(displayValue)
      : displayValue;

    changeField(key, storedValue as AlignmentSettings[typeof key]);

    // Force-set the corrected value so the display updates even when the
    // corrected value equals the current prop value (no SolidJS re-render).
    const stringValue = String(displayValue);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Force-updating web component value
    (eventTarget as any).value = stringValue;

    // Also update the host element when the target is the inner <input>
    const hostElement = eventTarget.closest('modus-wc-number-input');
    if (hostElement && hostElement !== eventTarget) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Force-updating web component host value
      (hostElement as any).value = stringValue;
    }
  };

  /** Commit on Enter key press. */
  const handleNumberKeyDown = (
    key: keyof AlignmentSettings,
    event: KeyboardEvent
  ): void => {
    if (event.key !== 'Enter') return;
    commitNumberValue(key, event.target as HTMLElement);
  };

  /** Commit on blur. */
  const handleNumberBlur = (
    key: keyof AlignmentSettings,
    event: Event
  ): void => {
    commitNumberValue(key, event.target as HTMLElement);
  };

  /** Commit on input change (e.g., spinner buttons). */
  const handleNumberChange = (
    key: keyof AlignmentSettings,
    event: Event
  ): void => {
    commitNumberValue(key, event.target as HTMLElement);
  };

  return (
    <div data-testid="alignment-settings" class="flex w-full flex-col gap-y-2">
      <style>
        {`
          /* Prevent 1px shift on focus by ensuring consistent box-sizing and preventing layout shift */
          [data-testid="alignment-settings"] modus-wc-select,
          [data-testid="alignment-settings"] modus-wc-number-input {
            box-sizing: border-box;
            display: block;
          }
          /* Ensure wrapper divs maintain consistent spacing */
          [data-testid="alignment-settings"] > div > div {
            min-width: 0;
          }
        `}
      </style>
      {/* Label Format & Label Symbol — side by side */}
      <div class="flex gap-4">
        <div class="flex-1">
          <modus-wc-select
            data-testid="label-format-select"
            size="md"
            bordered
            label={t('shared.ui.alignmentSettings.labelFormat')}
            inputTabIndex={1}
            class="w-full"
            options={formatSelectOptions()}
            value={props.settings.labelFormat}
            on:inputChange={(e: Event) =>
              changeField('labelFormat', getSelectValue(e) as LabelFormat)
            }
          />
        </div>

        <div class="flex-1">
          <modus-wc-select
            data-testid="label-symbol-select"
            size="md"
            bordered
            label={t('shared.ui.alignmentSettings.labelSymbol')}
            inputTabIndex={2}
            class="w-full"
            options={SYMBOL_OPTIONS}
            value={props.settings.labelSymbol}
            on:inputChange={(e: Event) =>
              handleSymbolChange(getSelectValue(e) as LabelSymbol)
            }
          />
        </div>
      </div>

      {/* Label offset (Z) */}
      <modus-wc-number-input
        data-testid="vertical-offset-input"
        size="md"
        bordered
        label={t('shared.ui.alignmentSettings.verticalOffset') + unitSuffix()}
        inputTabIndex={3}
        step={0.01}
        value={String(toDisplay(props.settings.verticalOffset))}
        onKeyDown={(e) => handleNumberKeyDown('verticalOffset', e)}
        on:inputBlur={(e: Event) => handleNumberBlur('verticalOffset', e)}
        on:inputChange={(e: Event) => handleNumberChange('verticalOffset', e)}
      />

      {/* Label horizontal offset */}
      <modus-wc-number-input
        data-testid="horizontal-offset-input"
        size="md"
        bordered
        label={t('shared.ui.alignmentSettings.horizontalOffset') + unitSuffix()}
        inputTabIndex={4}
        step={0.01}
        value={String(toDisplay(props.settings.horizontalOffset))}
        onKeyDown={(e) => handleNumberKeyDown('horizontalOffset', e)}
        on:inputBlur={(e: Event) => handleNumberBlur('horizontalOffset', e)}
        on:inputChange={(e: Event) => handleNumberChange('horizontalOffset', e)}
      />

      {/* Label Size */}
      <modus-wc-number-input
        data-testid="label-size-input"
        size="md"
        bordered
        label={t('shared.ui.alignmentSettings.labelSize')}
        inputTabIndex={5}
        step={0.1}
        min={MIN_LABEL_SIZE}
        value={String(props.settings.labelSize)}
        onKeyDown={(e) => handleNumberKeyDown('labelSize', e)}
        on:inputBlur={(e: Event) => handleNumberBlur('labelSize', e)}
        on:inputChange={(e: Event) => handleNumberChange('labelSize', e)}
      />

      {/* Label Interval */}
      <modus-wc-number-input
        data-testid="label-interval-input"
        size="md"
        bordered
        label={t('shared.ui.alignmentSettings.labelInterval') + unitSuffix()}
        inputTabIndex={6}
        step={1}
        min={MIN_LABEL_INTERVAL}
        value={String(toDisplay(props.settings.labelInterval))}
        onKeyDown={(e) => handleNumberKeyDown('labelInterval', e)}
        on:inputBlur={(e: Event) => handleNumberBlur('labelInterval', e)}
        on:inputChange={(e: Event) => handleNumberChange('labelInterval', e)}
      />

      {/* Coordinate Order */}
      <modus-wc-select
        data-testid="coordinate-order-select"
        size="md"
        bordered
        label={t('shared.ui.alignmentSettings.coordinateOrder')}
        inputTabIndex={7}
        options={coordinateOrderOptions()}
        value={props.settings.coordinateOrder}
        on:inputChange={(e: Event) =>
          changeField(
            'coordinateOrder',
            getSelectValue(e) as AlignmentCoordinateOrder
          )
        }
      />
    </div>
  );
};
